import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../../utils/authFetch';
import { BASE_URL } from '../../utils/config';
import i18n from '../../i18n';
import LanguageDropdown from '../../components/LanguageDropdown';

const ForgotPassword = ({ setHideFooter }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');

  useEffect(() => {
    setHideFooter(true);
    return () => {
      // Polling disabled to prevent property changes during animation
      setHideFooter(false);
    };
  }, [setHideFooter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please provide your email address.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await authFetch(`${BASE_URL}/users/forgot-password`, {
        method: 'POST',
        body: JSON.stringify({
          email: email
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || 'Password reset link sent to your email!', {
          autoClose: 5000,
          position: "bottom-right"
        });
        // Clear form
        setEmail('');
        setTimeout(() => {
          navigate('/auth/login?mode=login');
        }, 2000);
      } else {
        toast.error(data.message || 'Failed to send reset link. Please try again.');
      }
    } catch (error) {
      console.error('Error sending reset link:', error);
      toast.error('Error sending reset link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(2px)',
      WebkitBackdropFilter: 'blur(5px)'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
        maxWidth: '450px',
        width: '100%',
        textAlign: 'center',
        fontFamily: 'Arial, sans-serif',
        position: 'relative'  // Add this to contain the absolute positioned dropdown
      }}>
        <div style={{
          position: 'absolute',
          top: '15px',
          left: '15px',
          zIndex: 10
        }}>
          <LanguageDropdown currentLanguage={currentLanguage} setCurrentLanguage={setCurrentLanguage} />
        </div>
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{
            margin: '0 0 10px 0',
            color: '#333',
            fontSize: '28px',
            fontWeight: '600'
          }}>
            {t('forgot_password.title')}
          </h2>
          <p style={{
            color: '#666',
            fontSize: '16px',
            lineHeight: '1.5'
          }}>
            {t('forgot_password.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Email Input */}
          <div style={{ marginBottom: '20px', width: '100%', maxWidth: '400px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#333',
              fontWeight: '500',
              textAlign: 'center'
            }}>
              {t('commonEmail.email')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('commonEmail.enter_email')}
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e1e5e9',
                borderRadius: '6px',
                fontSize: '16px',
                outline: 'none',
                transition: 'border-color 0.3s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#29B185'}
              onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || !email}
            style={{
              backgroundColor: '#29B185',
              color: 'white',
              padding: '12px 30px',
              border: 'none',
              borderRadius: '6px',
              cursor: (isLoading || !email) ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              width: 'auto',
              minWidth: '200px',
              opacity: (isLoading || !email) ? 0.7 : 1,
              transition: 'all 0.3s ease',
              margin: '0 auto',
              display: 'block'
            }}
          >
            {isLoading ? t('commonEmail.sending') : t('forgot_password.send_reset_link')}
          </button>
        </form>

        <div style={{ marginTop: '30px' }}>
          <Link 
            to="/auth/login?mode=login" 
            style={{
              color: '#29B185',
              textDecoration: 'none',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            {t('forgot_password.back_to_login')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
